<template>
  <a-drawer
    :visible="visible"
    :width="1000"
    :title="null"
    placement="right"
    @close="handleClose"
    class="participant-detail-drawer"
  >
    <!-- 커스텀 헤더 -->
    <div class="detail-header">
      <div class="participant-info">
        <div class="main-info">
          <a-avatar :size="48" class="participant-avatar">
            <template #icon><i class="fa fa-user"></i></template>
          </a-avatar>
          <div class="info-text">
            <h2>
              참여자: {{ participant.customerName || '-' }}
              <span class="participant-id">(ID: {{ participant._id || participant.id || '-' }})</span>
            </h2>
            <div class="sub-info">
              <span><i class="fa fa-user"></i> 고객: {{ participant.customerInfo?.name || '-' }}</span>
              <a-divider type="vertical" />
              <span><i class="fa fa-phone"></i> 참여자: {{ participant.customerMobile || '-' }}</span>
              <a-divider type="vertical" />
              <span><i class="fa fa-phone-square"></i> 고객: {{ participant.customerInfo?.mobile || '-' }}</span>
              <a-divider type="vertical" />
              <span><i class="fa fa-hospital-o"></i> {{ participant.hospital || '-' }}</span>
            </div>
          </div>
        </div>
        <div class="status-info">
          <a-tag :color="getStatusColor(participant.status)">
            {{ getStatusText(participant.status) }}
          </a-tag>
          <span class="last-activity">
            마지막 활동: {{ formatRelativeTime(participant.lastActivity) }}
          </span>
        </div>
      </div>
    </div>


    <!-- 탭 컨텐츠 -->
    <a-tabs v-model:activeKey="activeTab" class="detail-tabs">
      <!-- 마이페이지 탭 (첫번째로 이동) -->
      <a-tab-pane key="mypage" tab="마이페이지">
        <div class="mypage-container">
          <!-- 헤더 영역 (스크린샷 스타일 참고) -->
          <div class="mypage-header">
            <div class="profile-section">
              <a-avatar :size="64" class="profile-avatar">
                <template #icon><i class="fa fa-user"></i></template>
              </a-avatar>
              <div class="profile-info">
                <h3>{{ myPageData?.name || '-' }}</h3>
                <p>{{ myPageData?.mobile || '-' }}</p>
              </div>
              <a-button
                size="small"
                @click="refreshMyPageData"
                :loading="myPageLoading"
                class="refresh-btn"
              >
                <i class="fa fa-refresh"></i>
              </a-button>
            </div>

            <!-- 빠른 메뉴 버튼들 (스크린샷 스타일) -->
            <div class="quick-menu" v-if="myPageData">
              <div class="menu-item">
                <div class="menu-icon coupon-icon">
                  <i class="fa fa-ticket"></i>
                  <span v-if="totalCouponCount > 0" class="badge">{{ totalCouponCount }}</span>
                </div>
                <span>내 쿠폰함</span>
              </div>
              <div class="menu-item">
                <div class="menu-icon booking-icon">
                  <i class="fa fa-calendar-check-o"></i>
                </div>
                <span>내 예약 정보</span>
              </div>
              <div class="menu-item">
                <div class="menu-icon cert-icon">
                  <i class="fa fa-certificate"></i>
                </div>
                <span>내 인증서</span>
              </div>
            </div>
          </div>

          <a-spin :spinning="myPageLoading">
            <div v-if="myPageData">
              <!-- 기본 정보 섹션 -->
              <a-card title="기본 정보" class="mb-3 info-card">
                <a-descriptions :column="2" bordered size="small">
                  <a-descriptions-item label="이름">{{ myPageData.name || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="전화번호">{{ myPageData.mobile || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="생년월일">{{ myPageData.birth || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="성별">{{ getGenderText(myPageData.gender) }}</a-descriptions-item>
                  <a-descriptions-item label="고객 ID">{{ myPageData._id || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="가입 경로">{{ getOriginSourceText(myPageData.originSource) }}</a-descriptions-item>
                  <a-descriptions-item label="가입일">{{ formatDateTime(myPageData.createAt) }}</a-descriptions-item>
                  <a-descriptions-item label="최종 수정일">{{ formatDateTime(myPageData.modifyAt) }}</a-descriptions-item>
                  <a-descriptions-item label="Klip 주소" :span="2">{{ myPageData.klipAddress || '-' }}</a-descriptions-item>
                </a-descriptions>
              </a-card>

              <!-- 마케팅 동의 정보 -->
              <a-card title="마케팅 동의 정보" class="mb-3 info-card" v-if="myPageData.marketingConsent">
                <a-descriptions :column="2" bordered size="small">
                  <a-descriptions-item label="동의 여부">
                    <a-tag :color="myPageData.marketingConsent.isAgreed ? 'green' : 'red'">
                      {{ myPageData.marketingConsent.isAgreed ? '동의' : '미동의' }}
                    </a-tag>
                  </a-descriptions-item>
                  <a-descriptions-item label="동의 일시">
                    {{ formatDateTime(myPageData.marketingConsent.agreedAt) }}
                  </a-descriptions-item>
                  <a-descriptions-item label="동의 출처">{{ myPageData.marketingConsent.source || '-' }}</a-descriptions-item>
                </a-descriptions>
              </a-card>

              <!-- 반려동물 정보 섹션 -->
              <a-card title="반려동물 정보" class="mb-3 info-card" v-if="myPageData.animalDtos && myPageData.animalDtos.length > 0">
                <div v-for="animal in myPageData.animalDtos" :key="animal.type" class="animal-section">
                  <h5>
                    <i :class="getAnimalIcon(animal.type)"></i>
                    {{ getAnimalTypeText(animal.type) }}
                  </h5>
                  <a-table
                    :columns="animalColumns"
                    :data-source="animal.attributes"
                    :pagination="false"
                    size="small"
                    class="mb-2"
                  >
                    <template #bodyCell="{ column, record }">
                      <template v-if="column.key === 'gender'">
                        {{ getGenderText(record.gender) }}
                      </template>
                      <template v-if="column.key === 'weight'">
                        {{ record.weight }}kg
                      </template>
                    </template>
                  </a-table>
                </div>
              </a-card>

              <!-- 병원 방문 이력 섹션 -->
              <a-card title="병원 방문 이력" class="mb-3 info-card" v-if="myPageData.companyMetadatas && myPageData.companyMetadatas.length > 0">
                <a-table
                  :columns="hospitalColumns"
                  :data-source="formattedHospitalData"
                  :pagination="false"
                  size="small"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'createAt'">
                      {{ formatDateTime(record.createAt) }}
                    </template>
                  </template>
                </a-table>
              </a-card>

              <!-- 미발급 쿠폰 섹션 -->
              <a-card title="미발급 쿠폰" class="mb-3 info-card" v-if="myPageData.unissuedCoupons && myPageData.unissuedCoupons.length > 0">
                <a-badge :count="myPageData.unissuedCoupons.length" :number-style="{ backgroundColor: '#faad14' }" />
                <a-table
                  :columns="unissuedCouponColumns"
                  :data-source="myPageData.unissuedCoupons"
                  :pagination="false"
                  size="small"
                  class="mt-2"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'participatedAt'">
                      {{ formatDateTime(record.participatedAt) }}
                    </template>
                    <template v-if="column.key === 'status'">
                      <a-tag color="orange">{{ getCouponStatusText(record.status) }}</a-tag>
                    </template>
                  </template>
                </a-table>
              </a-card>

              <!-- 발급된 쿠폰 섹션 -->
              <a-card title="발급된 쿠폰" class="mb-3 info-card" v-if="myPageData.issuedCoupons && myPageData.issuedCoupons.length > 0">
                <a-badge :count="myPageData.issuedCoupons.length" :number-style="{ backgroundColor: '#52c41a' }" />
                <a-table
                  :columns="issuedCouponColumns"
                  :data-source="myPageData.issuedCoupons"
                  :pagination="false"
                  size="small"
                  class="mt-2"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'issuedAt'">
                      {{ formatDateTime(record.issuedAt) }}
                    </template>
                    <template v-if="column.key === 'expiredAt'">
                      {{ formatDate(record.expiredAt) }}
                    </template>
                    <template v-if="column.key === 'status'">
                      <a-tag :color="getCouponStatusColor(record.status)">
                        {{ getCouponStatusText(record.status) }}
                      </a-tag>
                    </template>
                  </template>
                </a-table>
              </a-card>

              <!-- 최신 자이스 NFT 섹션 -->
              <a-card title="최신 자이스 NFT" class="mb-3 info-card" v-if="myPageData.latestZeissNft">
                <a-descriptions :column="2" bordered size="small">
                  <a-descriptions-item label="NFT ID">{{ myPageData.latestZeissNft._id || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="발급 상태">
                    <a-tag :color="myPageData.latestZeissNft.issueStatus === 'issued' ? 'green' : 'orange'">
                      {{ myPageData.latestZeissNft.issueStatus }}
                    </a-tag>
                  </a-descriptions-item>
                  <a-descriptions-item label="NFT 이름">{{ myPageData.latestZeissNft.itemName || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="병원명">{{ myPageData.latestZeissNft.companyName || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="수술일">{{ formatDate(myPageData.latestZeissNft.surgeryAt) }}</a-descriptions-item>
                  <a-descriptions-item label="수술 부위">
                    {{ myPageData.latestZeissNft.surgeryArea?.value || '-' }}
                    ({{ myPageData.latestZeissNft.surgeryArea?.productType || '-' }})
                  </a-descriptions-item>
                  <a-descriptions-item label="총 발행 수">{{ myPageData.latestZeissNft.totalPublishCount?.toLocaleString() || '-' }}번째</a-descriptions-item>
                  <a-descriptions-item label="발급일">{{ formatDateTime(myPageData.latestZeissNft.createAt) }}</a-descriptions-item>
                  <a-descriptions-item label="제조사">{{ myPageData.latestZeissNft.manufacturerName || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="Klip 주소" :span="2">{{ myPageData.latestZeissNft.customerKlipAddress || '-' }}</a-descriptions-item>
                </a-descriptions>
              </a-card>

              <!-- 원본 데이터 표시 -->
              <a-collapse class="mb-3">
                <a-collapse-panel key="1" header="원본 API 응답 데이터">
                  <pre class="json-viewer-simple">{{ JSON.stringify(myPageData, null, 2) }}</pre>
                </a-collapse-panel>
              </a-collapse>
            </div>

            <a-empty v-else-if="!myPageLoading && myPageError" :description="myPageError" />
            <a-empty v-else-if="!myPageLoading" description="상단의 새로고침 버튼을 클릭하여 고객 정보를 조회하세요." />
          </a-spin>
        </div>
      </a-tab-pane>

      <!-- 쿠폰 정보 탭 -->
      <a-tab-pane key="coupon" tab="쿠폰 정보">
        <div class="coupon-container">
          <h4 class="section-title">쿠폰 발급 및 사용 현황</h4>
          <a-empty v-if="!participant.couponInfo || participant.couponInfo.length === 0"
            description="발급된 쿠폰이 없습니다." />
          <a-table
            v-else
            :columns="couponColumns"
            :data-source="couponDataSource"
            :pagination="false"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'status'">
                <a-tag :color="getCouponStatusColor(record.status)">
                  {{ getCouponStatusText(record.status) }}
                </a-tag>
              </template>
              <template v-if="column.key === 'deleteYn'">
                <a-tag v-if="record.deleteYn === 'Y'" color="red">
                  삭제됨
                </a-tag>
                <a-tag v-else color="green">
                  정상
                </a-tag>
              </template>
              <template v-if="column.key === 'action'">
                <a-space>
                  <template v-if="record.deleteYn !== 'Y'">
                    <a-tooltip v-if="record.status === 'winner_selected'" title="쿠폰 재발송">
                      <a-button
                        size="small"
                        type="primary"
                        @click="() => handleResendCoupon(record)"
                        class="yellow-button"
                      >
                        재발송
                      </a-button>
                    </a-tooltip>
                    <a-tooltip v-else-if="record.status === 'COUPON_ISSUED'" title="이미 쿠폰을 수령했습니다">
                      <a-button
                        size="small"
                        disabled
                      >
                        재발송
                      </a-button>
                    </a-tooltip>
                    <a-button
                      v-else
                      size="small"
                      disabled
                    >
                      재발송
                    </a-button>
                    <a-tooltip title="쿠폰 삭제">
                      <a-button
                        size="small"
                        danger
                        @click="() => handleDeleteCoupon(record)"
                      >
                        <i class="fa fa-trash"></i>
                      </a-button>
                    </a-tooltip>
                  </template>
                  <template v-else>
                    <a-tooltip title="삭제된 쿠폰입니다">
                      <a-button
                        size="small"
                        disabled
                      >
                        삭제됨
                      </a-button>
                    </a-tooltip>
                  </template>
                </a-space>
              </template>
            </template>
          </a-table>
        </div>
      </a-tab-pane>

      <!-- 활동 타임라인 탭 -->
      <a-tab-pane key="timeline" tab="활동 타임라인">
        <div class="timeline-container">
          <h4 class="section-title">
            전체 활동 이력
            <a-radio-group
              v-model:value="timelineSort"
              size="small"
              class="timeline-sort-toggle"
              button-style="solid"
            >
              <a-radio-button value="asc">
                <i class="fa fa-sort-numeric-asc"></i> 시간순
              </a-radio-button>
              <a-radio-button value="desc">
                <i class="fa fa-sort-numeric-desc"></i> 최신순
              </a-radio-button>
            </a-radio-group>
          </h4>
          <a-empty v-if="!displayActivities || displayActivities.length === 0"
            description="기록된 활동이 없습니다." />
          <div v-else class="timeline-wrapper">
            <!-- 최신순일 때 상단에 종료 표시 -->
            <div v-if="timelineSort === 'desc'" class="timeline-end-top">
              <i class="fa fa-flag-checkered"></i> 종료
            </div>

            <!-- 시간순일 때 상단에 시작 표시 -->
            <div v-if="timelineSort === 'asc'" class="timeline-start-top">
              <i class="fa fa-flag"></i> 시작
            </div>

            <a-timeline mode="left">
              <a-timeline-item
                v-for="(activity, index) in displayActivities"
                :key="index"
                :color="getActivityColor(activity.type)"
              >
                <template #dot>
                  <div class="timeline-dot-wrapper">
                    <i :class="getActivityIcon(activity.type)"></i>
                  </div>
                </template>
                <div class="timeline-content">
                  <div class="activity-header">
                    <div class="activity-title-wrapper">
                      <div class="activity-type">{{ activity.type }}</div>
                      <div v-if="activity.details?.btnName" class="activity-btn-name">
                        {{ activity.details.btnName }}
                      </div>
                    </div>
                    <span class="activity-time">{{ formatDateTime(activity.timestamp) }}</span>
                  </div>
                  <div v-if="activity.detail" class="activity-detail">{{ activity.detail }}</div>
                  <div v-if="activity.details" class="activity-details">
                    <a-tag v-if="activity.details.action" size="small" color="blue">
                      액션: {{ activity.details.action }}
                    </a-tag>
                    <a-tag v-if="activity.details.shareChannel && activity.type !== '카카오톡 공유하기'" size="small" color="green">
                      채널: {{ activity.details.shareChannel }}
                    </a-tag>
                    <a-tag v-if="activity.details.eligible !== undefined && activity.type !== '카카오톡 공유하기'" size="small" :color="activity.details.eligible ? 'green' : 'red'">
                      {{ activity.details.eligible ? '응모 완료' : '응모 대상 아님' }}
                    </a-tag>
                  </div>
                  <div v-if="activity.metadata" class="activity-metadata">
                    <a-tag v-for="(value, key) in activity.metadata" :key="key" size="small">
                      {{ key }}: {{ value }}
                    </a-tag>
                  </div>
                </div>
              </a-timeline-item>
            </a-timeline>

            <!-- 시간순일 때 하단에 종료 표시 -->
            <div v-if="timelineSort === 'asc'" class="timeline-end-bottom">
              <i class="fa fa-flag-checkered"></i> 종료
            </div>

            <!-- 최신순일 때 하단에 시작 표시 -->
            <div v-if="timelineSort === 'desc'" class="timeline-start-bottom">
              <i class="fa fa-flag"></i> 시작
            </div>
          </div>
        </div>
      </a-tab-pane>

      <!-- 리뷰 정보 탭 -->
      <a-tab-pane key="reviews" tab="리뷰 정보">
        <div class="review-container">
          <h4 class="section-title">
            리뷰 작성 현황
            <a-badge :count="participant.reviewCount || 0" :number-style="{ backgroundColor: '#52c41a' }" style="margin-left: 8px" />
          </h4>
          <a-empty v-if="!participant.reviews || participant.reviews.length === 0"
            description="작성된 리뷰가 없습니다." />
          <div v-else class="reviews-list">
            <a-card
              v-for="(review, index) in participant.reviews"
              :key="review.reviewId || index"
              class="review-card"
              :bordered="true"
            >
              <div class="review-header">
                <div class="review-info">
                  <a-tag color="blue">{{ review.companyName || review.eventName || '병원명 없음' }}</a-tag>
                  <a-rate :value="review.score || 0" disabled />
                  <span class="review-score">{{ review.score || 0 }}점</span>
                </div>
                <div class="review-date">
                  {{ formatDateTime(review.createdAt) }}
                </div>
              </div>

              <div v-if="review.content" class="review-content">
                <p>{{ review.content }}</p>
              </div>
              <div v-else class="review-content no-content">
                <p><i>리뷰 내용이 없습니다.</i></p>
              </div>

              <div v-if="review.satisfactionPoints && review.satisfactionPoints.length > 0" class="satisfaction-points">
                <div class="points-label">만족 포인트:</div>
                <a-tag v-for="point in review.satisfactionPoints" :key="point" color="green">
                  {{ point }}
                </a-tag>
              </div>

              <div class="review-meta">
                <a-space split="|">
                  <span v-if="review.manufacturerName">
                    <i class="fa fa-industry"></i> {{ review.manufacturerName }}
                  </span>
                  <span v-if="review.nftId">
                    <i class="fa fa-certificate"></i> NFT: {{ review.nftId }}
                  </span>
                  <span v-if="review.updatedAt && review.updatedAt !== review.createdAt">
                    <i class="fa fa-edit"></i> 수정: {{ formatDateTime(review.updatedAt) }}
                  </span>
                </a-space>
              </div>
            </a-card>
          </div>
        </div>
      </a-tab-pane>

      <!-- 메시지 이력 탭 -->
      <a-tab-pane key="messages" tab="메시지 이력">
        <div class="message-container">
          <h4 class="section-title">
            메시지 발송 이력
            <a-radio-group
              v-model:value="messageSort"
              size="small"
              class="timeline-sort-toggle"
              button-style="solid"
            >
              <a-radio-button value="asc">
                <i class="fa fa-sort-numeric-asc"></i> 시간순
              </a-radio-button>
              <a-radio-button value="desc">
                <i class="fa fa-sort-numeric-desc"></i> 최신순
              </a-radio-button>
            </a-radio-group>
          </h4>
          <a-empty v-if="!displayMessages || displayMessages.length === 0"
            description="발송된 메시지가 없습니다." />
          <div v-else class="timeline-wrapper">
            <!-- 최신순일 때 상단에 종료 표시 -->
            <div v-if="messageSort === 'desc'" class="timeline-end-top">
              <i class="fa fa-flag-checkered"></i> 최근
            </div>

            <!-- 시간순일 때 상단에 시작 표시 -->
            <div v-if="messageSort === 'asc'" class="timeline-start-top">
              <i class="fa fa-flag"></i> 시작
            </div>

            <a-timeline mode="left">
              <a-timeline-item
                v-for="(message, index) in displayMessages"
                :key="index"
                :color="getMessageStatusColor(message.status)"
              >
                <template #dot>
                  <div class="timeline-dot-wrapper">
                    <i :class="getMessageStatusIcon(message.status)"></i>
                  </div>
                </template>
                <div class="timeline-content">
                  <div class="activity-header">
                    <div class="activity-title-wrapper">
                      <div class="activity-type">{{ message.type || '메시지 발송' }}</div>
                      <a-tag size="small" :color="getMessageStatusColor(message.status)">
                        {{ getMessageStatusText(message.status) }}
                      </a-tag>
                    </div>
                    <span class="activity-time">{{ formatDateTime(message.sentAt) }}</span>
                  </div>
                  <div class="message-info">
                    <div v-if="message.templateName" class="message-template">
                      <i class="fa fa-file-text-o"></i> 템플릿: {{ message.templateName }}
                    </div>
                    <div v-if="message.content" class="message-content">
                      <div class="message-text">{{ message.content }}</div>
                    </div>
                    <div v-if="message.clickedAt" class="message-interaction">
                      <a-tag size="small" color="green">
                        <i class="fa fa-mouse-pointer"></i> 클릭됨: {{ formatDateTime(message.clickedAt) }}
                      </a-tag>
                    </div>
                    <div v-if="message.viewedAt" class="message-interaction">
                      <a-tag size="small" color="blue">
                        <i class="fa fa-eye"></i> 열람됨: {{ formatDateTime(message.viewedAt) }}
                      </a-tag>
                    </div>
                    <div v-if="message.failedReason" class="message-error">
                      <a-alert
                        :message="`실패 사유: ${message.failedReason}`"
                        type="error"
                        show-icon
                        banner
                      />
                    </div>
                  </div>
                </div>
              </a-timeline-item>
            </a-timeline>

            <!-- 시간순일 때 하단에 종료 표시 -->
            <div v-if="messageSort === 'asc'" class="timeline-end-bottom">
              <i class="fa fa-flag-checkered"></i> 최근
            </div>

            <!-- 최신순일 때 하단에 시작 표시 -->
            <div v-if="messageSort === 'desc'" class="timeline-start-bottom">
              <i class="fa fa-flag"></i> 시작
            </div>
          </div>
        </div>
      </a-tab-pane>

      <!-- 원본 데이터 탭 -->
      <a-tab-pane key="rawdata" tab="원본 데이터">
        <div class="rawdata-container">
          <h4 class="section-title">
            참여자 원본 데이터 (JSON)
            <a-space style="float: right;">
              <a-button
                size="small"
                @click="toggleAllJson"
              >
                <i :class="jsonExpanded ? 'fa fa-compress' : 'fa fa-expand'"></i>
                {{ jsonExpanded ? '모두 접기' : '모두 펼치기' }}
              </a-button>
              <a-button
                size="small"
                @click="copyRawData"
              >
                <i class="fa fa-copy"></i> 복사
              </a-button>
            </a-space>
          </h4>
          <div
            class="json-viewer"
            v-html="formattedJsonHtml"
            @click="handleJsonClick"
          ></div>
        </div>
      </a-tab-pane>
    </a-tabs>
  </a-drawer>
</template>

<script>
import { ref, computed, watch, watchEffect, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';
import { twoBtnDialog } from '@/utils/utils';
import eventsApis from '../api.js';

dayjs.extend(relativeTime);
dayjs.locale('ko');

export default {
  name: 'ParticipantDetailDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    participant: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'coupon-deleted', 'coupon-resent'],
  setup(props, { emit }) {
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiMethods = null;
    
    if (instance && instance.proxy) {
      apiMethods = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
    }
    
    const activeTab = ref('mypage');
    const timelineSort = ref('asc'); // 기본값을 시간순(오래된 것 -> 최신)으로 설정
    const messageSort = ref('asc'); // 메시지 정렬 기본값
    const jsonExpanded = ref(true);
    const collapsedPaths = ref(new Set());
    
    // 마이페이지 관련 상태
    const myPageLoading = ref(false);
    const myPageData = ref(null);
    const myPageError = ref(null);
    
    // 드로어가 열릴 때 마이페이지 데이터 자동 로드
    watch(() => props.visible, (newVal) => {
      if (newVal && props.participant?.customerId) {
        // 드로어가 열리고 고객 ID가 있으면 마이페이지 데이터 로드
        loadMyPageData();
      } else if (!newVal) {
        // 드로어가 닫히면 데이터 초기화
        myPageData.value = null;
        myPageError.value = null;
      }
    });
    
    // participant 변경 시에도 데이터 재로드
    watch(() => props.participant?.customerId, (newCustomerId) => {
      if (props.visible && newCustomerId) {
        loadMyPageData();
      }
    });
    
    // 메트릭 계산
    const metrics = computed(() => {
      const p = props.participant;
      return {
        visitRate: p.visited ? 100 : 0,
        shareRate: p.sharedAt ? 60 : 0,
        participationRate: p.couponReceived ? 30 : 0,
        completionRate: p.loggedIn ? 10 : 0
      };
    });
    
    // 현재 단계 계산
    const getCurrentStep = () => {
      const p = props.participant;
      if (p.loggedIn) return 3;
      if (p.couponReceived) return 2;
      if (p.sharedAt) return 1;
      return 0;
    };
    
    // 단계 진행률 계산
    const getStepPercent = () => {
      const step = getCurrentStep();
      return (step + 1) * 25;
    };
    
    // 친구 참여 수
    const friendParticipationCount = computed(() => {
      return props.participant.friendParticipation || 0;
    });
    
    // 쿠폰 사용/전체 수
    const couponUsedCount = computed(() => {
      if (!props.participant.couponInfo) return 0;
      return props.participant.couponInfo.filter(c => c.status === 'used').length;
    });
    
    const couponTotalCount = computed(() => {
      return props.participant.couponInfo?.length || 0;
    });
    
    // 활동 점수 계산
    const activityScore = computed(() => {
      let score = 0;
      const p = props.participant;
      
      // 접속: 10점
      if (p.visited) score += 10;
      
      // 공유: 회당 5점 (최대 50점)
      const shareScore = Math.min((p.shareCount || 0) * 5, 50);
      score += shareScore;
      
      // 친구 참여: 인당 10점
      score += (p.friendParticipation || 0) * 10;
      
      // 쿠폰 사용: 개당 20점
      score += couponUsedCount.value * 20;
      
      return score;
    });
    
    // 포맷된 JSON HTML (접기/펼치기 가능)
    const formattedJsonHtml = computed(() => {
      if (!props.participant) return '<span>{}</span>';
      
      let pathCounter = 0;
      let lineNumber = 1;
      
      const formatValue = (obj, indent = 0, path = '', isLast = true) => {
        if (obj === null) return '<span class="json-null">null</span>';
        if (obj === undefined) return '<span class="json-undefined">undefined</span>';
        
        const type = typeof obj;
        
        if (type === 'string') {
          // 문자열 이스케이프 처리
          const escaped = obj.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          return `<span class="json-string">"${escaped}"</span>`;
        }
        if (type === 'number') {
          return `<span class="json-number">${obj}</span>`;
        }
        if (type === 'boolean') {
          return `<span class="json-boolean">${obj}</span>`;
        }
        
        if (Array.isArray(obj)) {
          if (obj.length === 0) return '<span class="json-bracket">[]</span>';
          
          const currentPath = `path-${pathCounter++}`;
          const isCollapsed = collapsedPaths.value.has(currentPath);
          const spaces = ' '.repeat(indent * 2);
          const innerSpaces = ' '.repeat((indent + 1) * 2);
          
          let result = '';
          
          // 토글 버튼과 여는 브래킷
          result += `<span class="json-toggle" data-path="${currentPath}">${isCollapsed ? '▶' : '▼'}</span>`;
          result += `<span class="json-bracket">[</span>`;
          
          if (isCollapsed) {
            result += `<span class="json-ellipsis">...</span><span class="json-bracket">]</span>`;
          } else {
            result += '\n';
            obj.forEach((item, index) => {
              const isLastItem = index === obj.length - 1;
              result += innerSpaces + formatValue(item, indent + 1, currentPath, isLastItem);
              if (!isLastItem) result += ',';
              result += '\n';
            });
            result += spaces + '<span class="json-bracket">]</span>';
          }
          
          return result;
        }
        
        if (type === 'object') {
          const keys = Object.keys(obj);
          if (keys.length === 0) return '<span class="json-bracket">{}</span>';
          
          const currentPath = `path-${pathCounter++}`;
          const isCollapsed = collapsedPaths.value.has(currentPath);
          const spaces = ' '.repeat(indent * 2);
          const innerSpaces = ' '.repeat((indent + 1) * 2);
          
          let result = '';
          
          // 토글 버튼과 여는 브래킷
          result += `<span class="json-toggle" data-path="${currentPath}">${isCollapsed ? '▶' : '▼'}</span>`;
          result += `<span class="json-bracket">{</span>`;
          
          if (isCollapsed) {
            result += `<span class="json-ellipsis">...</span><span class="json-bracket">}</span>`;
          } else {
            result += '\n';
            keys.forEach((key, index) => {
              const isLastItem = index === keys.length - 1;
              const value = obj[key];
              const isExpandable = value !== null && typeof value === 'object';
              
              result += innerSpaces;
              result += `<span class="json-key">"${key}"</span>: `;
              result += formatValue(value, indent + 1, currentPath, isLastItem);
              if (!isLastItem) result += ',';
              result += '\n';
            });
            result += spaces + '<span class="json-bracket">}</span>';
          }
          
          return result;
        }
        
        return String(obj);
      };
      
      return `<pre>${formatValue(props.participant)}</pre>`;
    });
    
    // 쿠폰 테이블 컬럼
    const couponColumns = [
      {
        title: '쿠폰명',
        dataIndex: 'name',
        key: 'name',
        width: '20%'
      },
      {
        title: '쿠폰번호',
        dataIndex: 'couponNumber',
        key: 'couponNumber',
        width: '18%'
      },
      {
        title: '발급일',
        dataIndex: 'issuedAt',
        key: 'issuedAt',
        width: '13%'
      },
      {
        title: '만료일',
        dataIndex: 'expiredAt',
        key: 'expiredAt',
        width: '13%'
      },
      {
        title: '상태',
        key: 'status',
        width: '13%'
      },
      {
        title: '삭제여부',
        key: 'deleteYn',
        width: '13%'
      },
      {
        title: '액션',
        key: 'action',
        width: '10%',
        align: 'center'
      }
    ];
    
    // 쿠폰 데이터 소스
    const couponDataSource = computed(() => {
      if (!props.participant.couponInfo) return [];
      
      return props.participant.couponInfo.map((coupon, index) => {
        // 실제 API 상태값을 그대로 사용
        return {
          key: index,
          name: coupon.goodsInfo?.goodsName || coupon.goodsName || '스타벅스 아메리카노',
          couponNumber: coupon.couponNumber || '-',
          issuedAt: formatDate(coupon.couponIssuedAt || coupon.createdAt),
          expiredAt: formatDate(coupon.couponExpiredAt || coupon.goodsInfo?.limitDate),
          status: coupon.status, // winner_selected 또는 COUPON_ISSUED
          originalStatus: coupon.status,
          deleteYn: coupon.deleteYn || 'N', // 삭제 여부 추가 (Y/N)
          ...coupon
        };
      });
    });
    
    // 정렬된 활동 목록 (정렬 옵션에 따라 변경)
    const displayActivities = computed(() => {
      if (!props.participant.activities) return [];
      
      const activities = [...props.participant.activities].map(activity => ({
        ...activity,
        details: activity.details || {}, // details 객체 추가
        detail: getActivityDetail(activity),
        metadata: getActivityMetadata(activity)
      }));
      
      // 정렬 방향에 따라 정렬
      if (timelineSort.value === 'desc') {
        // 최신순 (최근 -> 과거)
        return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      } else {
        // 시간순 (과거 -> 최근)
        return activities.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      }
    });
    
    // 정렬된 메시지 목록
    const displayMessages = computed(() => {
      if (!props.participant.messageSendHistory || props.participant.messageSendHistory.length === 0) {
        return [];
      }
      
      const messages = [...props.participant.messageSendHistory];
      
      // 정렬 방향에 따라 정렬
      if (messageSort.value === 'desc') {
        // 최신순 (최근 -> 과거)
        return messages.sort((a, b) => new Date(b.sentAt || b.createdAt) - new Date(a.sentAt || a.createdAt));
      } else {
        // 시간순 (과거 -> 최근)
        return messages.sort((a, b) => new Date(a.sentAt || a.createdAt) - new Date(b.sentAt || b.createdAt));
      }
    });
    
    // 메시지 통계 계산
    const messageStats = computed(() => {
      if (!props.participant.messageSendHistory || props.participant.messageSendHistory.length === 0) {
        return null;
      }
      
      const messages = props.participant.messageSendHistory;
      return {
        total: messages.length,
        sent: messages.filter(m => m.status === 'sent' || m.status === 'success').length,
        viewed: messages.filter(m => m.status === 'viewed' || m.viewedAt).length,
        clicked: messages.filter(m => m.status === 'clicked' || m.clickedAt).length,
        failed: messages.filter(m => m.status === 'failed').length
      };
    });
    
    // Helper 함수들
    const handleClose = () => {
      emit('close');
    };
    
    const getStatusColor = (status) => {
      const colors = {
        participated: 'default',
        shared: 'blue',
        coupon_pending: 'orange',
        coupon_received: 'green',
        logged_in: 'purple'
      };
      return colors[status] || 'default';
    };
    
    const getStatusText = (status) => {
      const texts = {
        participated: '참여',
        shared: '공유 완료',
        coupon_pending: '쿠폰 수령 대기',
        coupon_received: '쿠폰 수령 완료',
        logged_in: '로그인 완료'
      };
      return texts[status] || status;
    };
    
    const formatRelativeTime = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).fromNow();
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD');
    };
    
    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss');
    };
    
    const getAvatarColor = (status) => {
      return status === 'completed' ? '#52c41a' : '#faad14';
    };
    
    const getCouponStatusType = (status) => {
      const types = {
        pending: 'warning',
        issued: 'processing',
        used: 'success',
        expired: 'error'
      };
      return types[status] || 'default';
    };
    
    const getCouponStatusText = (status) => {
      const texts = {
        'winner_selected': '수령 대기',
        'COUPON_ISSUED': '수령 완료',
        'used': '사용 완료',
        'expired': '만료'
      };
      return texts[status] || status;
    };
    
    const getCouponStatusColor = (status) => {
      const colors = {
        'winner_selected': 'orange',
        'COUPON_ISSUED': 'green',
        'used': 'blue',
        'expired': 'red'
      };
      return colors[status] || 'default';
    };
    
    const getActivityColor = (type) => {
      const colors = {
        '카카오톡 공유하기': '#fee500',
        '링크 복사': 'green',
        '쿠폰 수령': 'blue',
        '로그인': 'purple',
        '이벤트 참여': 'orange'
      };
      return colors[type] || 'gray';
    };
    
    const getActivityIcon = (type) => {
      const icons = {
        '카카오톡 공유하기': 'fa fa-comment',
        '링크 복사': 'fa fa-link',
        '쿠폰 수령': 'fa fa-ticket',
        '로그인': 'fa fa-sign-in',
        '이벤트 참여': 'fa fa-user-plus'
      };
      return icons[type] || 'fa fa-circle';
    };
    
    const getActivityDetail = (activity) => {
      // 활동 타입에 따른 상세 설명 생성
      if (activity.type === '카카오톡 공유하기') {
        return '카카오톡으로 이벤트를 공유했습니다.';
      }
      if (activity.type === '쿠폰 수령') {
        return '이벤트 쿠폰을 수령했습니다.';
      }
      return null;
    };
    
    const getActivityMetadata = (activity) => {
      // 활동 타입에 따른 메타데이터 생성
      if (activity.metadata) {
        return activity.metadata;
      }
      return null;
    };
    
    // 메시지 상태별 색상
    const getMessageStatusColor = (status) => {
      const colors = {
        requested: 'orange',
        sent: 'green',
        viewed: 'blue',
        clicked: 'purple',
        failed: 'red'
      };
      return colors[status] || 'default';
    };
    
    // 메시지 상태별 텍스트
    const getMessageStatusText = (status) => {
      const texts = {
        requested: '발송 대기',
        sent: '발송 완료',
        viewed: '열람됨',
        clicked: '클릭됨',
        failed: '발송 실패'
      };
      return texts[status] || status;
    };
    
    // 메시지 상태별 아이콘
    const getMessageStatusIcon = (status) => {
      const icons = {
        requested: 'fa fa-clock-o',
        sent: 'fa fa-check',
        viewed: 'fa fa-eye',
        clicked: 'fa fa-mouse-pointer',
        failed: 'fa fa-exclamation-circle'
      };
      return icons[status] || 'fa fa-envelope';
    };
    
    const handleSendCoupon = (coupon) => {
      message.info(`쿠폰 발송 기능 준비중입니다.`);
      // API 호출 로직 추가 - 쿠폰 번호 생성 및 발송
    };
    
    const handleResendCoupon = (coupon) => {
      // 참여자 ID와 이벤트 ID 확인
      const attendeeId = props.participant._id || props.participant.id;
      const eventId = props.participant.eventId || coupon.eventId;
      
      if (!attendeeId || !eventId) {
        message.error('필요한 정보가 부족합니다.');
        return;
      }
      
      // 당첨알림 발송 API 호출
      if (apiMethods && apiMethods.sendWinnerNotifications) {
        apiMethods.sendWinnerNotifications(
          eventId,
          {
            messageType: 'ALIM_TALK',
            attendeeIds: [attendeeId]
          },
          (response) => {
            // API 응답 구조 분석
            const responseData = response.data?.data || response.data;
            const results = responseData.results || [];
            const successCount = responseData.successCount || 0;
            const failedCount = responseData.failedCount || 0;
            
            // 결과 메시지 처리
            if (successCount > 0) {
              message.success('당첨알림이 재발송되었습니다.');
              // 부모 컴포넌트에 이벤트 발생시켜 데이터 새로고침
              emit('coupon-resent', coupon);
            } else if (failedCount > 0 && results.length > 0) {
              // 실패한 경우 실패 사유 표시
              const failReason = results[0].reason || '알 수 없는 오류';
              message.warning(`당첨알림 재발송 실패: ${failReason}`);
            } else {
              // 처리된 건이 없는 경우
              const summary = responseData.summary;
              message.warning(summary || '처리 가능한 상태가 아닙니다');
            }
          },
          (error) => {
            message.error('당첨알림 재발송 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
          }
        );
      } else {
        message.error('API 초기화 오류가 발생했습니다.');
      }
    };
    
    // 마이페이지 관련 메서드
    const viewMyPage = () => {
      // 마이페이지 탭으로 이동
      activeTab.value = 'mypage';
      // 데이터가 없을 때만 로드 (이미 드로어 열릴 때 로드했으므로)
      if (!myPageData.value) {
        loadMyPageData();
      }
    };
    
    const loadMyPageData = async () => {
      if (!props.participant.customerId) {
        myPageError.value = '고객 ID가 없습니다.';
        return;
      }
      
      // 이미 로딩 중이면 중복 호출 방지
      if (myPageLoading.value) {
        return;
      }
      
      myPageLoading.value = true;
      myPageError.value = null;
      
      try {
        // API 호출을 위한 axios 인스턴스 가져오기
        const apiInstance = instance?.proxy?.$apiInstance;
        if (!apiInstance) {
          throw new Error('API 인스턴스를 찾을 수 없습니다.');
        }
        
        // 관리자 토큰으로 고객 정보 조회 API 호출
        const response = await apiInstance.get('/customers/me', {
          params: {
            customerId: props.participant.customerId
          }
        });
        
        if (response.data && response.data.data) {
          myPageData.value = response.data.data;
        } else {
          myPageData.value = response.data;
        }
        
        console.log('마이페이지 데이터 로드 완료:', myPageData.value);
      } catch (error) {
        console.error('마이페이지 데이터 로드 실패:', error);
        myPageError.value = error.response?.data?.message || '고객 정보를 불러오는 중 오류가 발생했습니다.';
        // 에러 메시지는 마이페이지 탭을 열었을 때만 표시
        if (activeTab.value === 'mypage') {
          message.error(myPageError.value);
        }
      } finally {
        myPageLoading.value = false;
      }
    };
    
    const refreshMyPageData = () => {
      loadMyPageData();
    };
    
    // 마이페이지 테이블 컬럼 정의
    const animalColumns = [
      { title: '이름', dataIndex: 'name', key: 'name' },
      { title: '품종', dataIndex: 'variety', key: 'variety' },
      { title: '성별', key: 'gender', width: '80px' },
      { title: '체중', key: 'weight', width: '80px' },
      { title: '생년월일', dataIndex: 'birthDate', key: 'birthDate' }
    ];
    
    const hospitalColumns = [
      { title: '병원명', dataIndex: 'name', key: 'name' },
      { title: '방문일', key: 'createAt' }
    ];
    
    const unissuedCouponColumns = [
      { title: '이벤트명', dataIndex: 'eventTitle', key: 'eventTitle' },
      { title: '참여일', key: 'participatedAt' },
      { title: '상태', key: 'status', width: '100px' }
    ];
    
    const issuedCouponColumns = [
      { title: '쿠폰명', dataIndex: 'couponName', key: 'couponName' },
      { title: '쿠폰번호', dataIndex: 'couponNumber', key: 'couponNumber' },
      { title: '발급일', key: 'issuedAt' },
      { title: '만료일', key: 'expiredAt' },
      { title: '상태', key: 'status', width: '100px' }
    ];
    
    const getEventStatusTagColor = (status) => {
      const colors = {
        'completed': 'green',
        'in_progress': 'blue',
        'participated': 'orange'
      };
      return colors[status] || 'default';
    };
    
    // 성별 텍스트 변환
    const getGenderText = (gender) => {
      const genderMap = {
        'M': '남성',
        'F': '여성',
        'MALE': '남성',
        'FEMALE': '여성'
      };
      return genderMap[gender] || gender || '-';
    };
    
    // 가입 경로 텍스트 변환
    const getOriginSourceText = (source) => {
      const sourceMap = {
        'CUSTOMER_WEB_REGISTER': '웹 가입',
        'APP_REGISTER': '앱 가입',
        'ADMIN_REGISTER': '관리자 등록'
      };
      return sourceMap[source] || source || '-';
    };
    
    // 동물 타입 텍스트 변환
    const getAnimalTypeText = (type) => {
      const typeMap = {
        'DOG': '강아지',
        'CAT': '고양이',
        'BIRD': '새',
        'FISH': '물고기',
        'HAMSTER': '햄스터',
        'RABBIT': '토끼'
      };
      return typeMap[type] || type || '-';
    };
    
    // 동물 아이콘 가져오기
    const getAnimalIcon = (type) => {
      const iconMap = {
        'DOG': 'fa fa-paw',
        'CAT': 'fa fa-paw',
        'BIRD': 'fa fa-twitter',
        'FISH': 'fa fa-fish',
        'HAMSTER': 'fa fa-paw',
        'RABBIT': 'fa fa-paw'
      };
      return iconMap[type] || 'fa fa-paw';
    };
    
    // 병원 방문 이력 데이터 포맷
    const formattedHospitalData = computed(() => {
      if (!myPageData.value?.companyMetadatas) return [];
      return myPageData.value.companyMetadatas.map((item, index) => ({
        key: index,
        ...item
      }));
    });
    
    // 전체 쿠폰 수 계산
    const totalCouponCount = computed(() => {
      if (!myPageData.value) return 0;
      const unissued = myPageData.value.unissuedCoupons?.length || 0;
      const issued = myPageData.value.issuedCoupons?.length || 0;
      return unissued + issued;
    });
    
    const handleDeleteCoupon = async (coupon) => {
      // 쿠폰 상태에 따른 메시지 분기
      let dialogTitle = '쿠폰 삭제';
      let dialogMessage = '';
      
      if (coupon.status === 'winner_selected') {
        // 수령 대기 상태인 경우
        dialogMessage = `쿠폰 발급 권한을 제거하시겠습니까?`;
      } else {
        // 그 외 상태인 경우
        dialogMessage = `쿠폰 번호 "${coupon.couponNumber || '-'}"를 삭제하시겠습니까?\n삭제된 쿠폰은 더 이상 사용할 수 없습니다.`;
      }
      
      // 삭제 확인 다이얼로그
      const isConfirmed = await twoBtnDialog(
        dialogTitle,
        dialogMessage,
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      // API 호출
      if (apiMethods && apiMethods.removeCoupons) {
        const attendeeId = props.participant._id || props.participant.id;
        const eventId = props.participant.eventId || coupon.eventId;
        
        if (!attendeeId || !eventId || !coupon.couponNumber) {
          message.error('필요한 정보가 부족합니다.');
          return;
        }
        
        apiMethods.removeCoupons(
          eventId,
          [attendeeId],
          coupon.couponNumber,
          (response) => {
            message.success('쿠폰이 삭제되었습니다.');
            // 부모 컴포넌트에 이벤트 발생시켜 데이터 새로고침
            emit('coupon-deleted', coupon);
          },
          (error) => {
            message.error('쿠폰 삭제 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
          }
        );
      } else {
        message.error('API 초기화 오류가 발생했습니다.');
      }
    };
    
    const handleRevokeCoupon = (coupon) => {
      message.warning(`쿠폰 회수: ${coupon.name}`);
      // API 호출 로직 추가
    };
    
    // 원본 데이터 복사
    const copyRawData = () => {
      const jsonStr = JSON.stringify(props.participant, null, 2);
      navigator.clipboard.writeText(jsonStr).then(() => {
        message.success('원본 데이터가 클립보드에 복사되었습니다.');
      }).catch(() => {
        message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
      });
    };
    
    // JSON 클릭 처리 (접기/펼치기)
    const handleJsonClick = (event) => {
      const target = event.target;
      // json-toggle 클래스를 가진 요소 클릭 시 처리
      if (target.classList.contains('json-toggle')) {
        const path = target.getAttribute('data-path');
        if (path) {
          if (collapsedPaths.value.has(path)) {
            collapsedPaths.value.delete(path);
          } else {
            collapsedPaths.value.add(path);
          }
        }
      }
    };
    
    // 모두 접기/펼치기
    const toggleAllJson = () => {
      if (jsonExpanded.value) {
        // 모두 접기 - 모든 경로를 collapsed에 추가
        const allPaths = document.querySelectorAll('.json-toggle');
        allPaths.forEach(el => {
          const path = el.getAttribute('data-path');
          if (path) collapsedPaths.value.add(path);
        });
      } else {
        // 모두 펼치기 - collapsed 초기화
        collapsedPaths.value.clear();
      }
      jsonExpanded.value = !jsonExpanded.value;
    };
    
    return {
      activeTab,
      timelineSort,
      messageSort,
      metrics,
      getCurrentStep,
      getStepPercent,
      friendParticipationCount,
      couponUsedCount,
      couponTotalCount,
      activityScore,
      formattedJsonHtml,
      jsonExpanded,
      couponColumns,
      couponDataSource,
      displayActivities,
      displayMessages,
      messageStats,
      handleClose,
      getStatusColor,
      getStatusText,
      formatRelativeTime,
      formatDate,
      formatDateTime,
      getAvatarColor,
      getCouponStatusType,
      getCouponStatusText,
      getCouponStatusColor,
      getActivityColor,
      getActivityIcon,
      getMessageStatusColor,
      getMessageStatusText,
      getMessageStatusIcon,
      handleSendCoupon,
      handleResendCoupon,
      handleDeleteCoupon,
      handleRevokeCoupon,
      copyRawData,
      handleJsonClick,
      toggleAllJson,
      // 마이페이지 관련
      myPageLoading,
      myPageData,
      myPageError,
      viewMyPage,
      loadMyPageData,
      refreshMyPageData,
      animalColumns,
      hospitalColumns,
      unissuedCouponColumns,
      issuedCouponColumns,
      getEventStatusTagColor,
      getGenderText,
      getOriginSourceText,
      getAnimalTypeText,
      getAnimalIcon,
      formattedHospitalData,
      totalCouponCount
    };
  }
};
</script>

<style scoped>
.participant-detail-drawer :deep(.ant-drawer-body) {
  padding: 0;
}

.detail-header {
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.participant-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.main-info {
  display: flex;
  gap: 16px;
  align-items: center;
}

.participant-avatar {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.info-text h2 {
  margin: 0;
  color: white;
  font-size: 20px;
  font-weight: 600;
}

.participant-id {
  font-size: 14px;
  font-weight: 400;
  opacity: 0.9;
  margin-left: 8px;
}

.sub-info {
  margin-top: 8px;
  opacity: 0.95;
  font-size: 14px;
}

.sub-info i {
  margin-right: 4px;
}

.status-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.last-activity {
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
}


.detail-tabs {
  padding: 0 24px 24px;
}

.detail-tabs :deep(.ant-tabs-nav) {
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #262626;
}


/* 쿠폰 정보 스타일 */
.coupon-container {
  padding: 16px 0;
}

/* 타임라인 스타일 */
.timeline-container {
  padding: 16px 0;
  max-height: calc(100vh - 300px); /* 브라우저 높이에서 헤더와 탭 높이를 뺀 값 */
  overflow-y: auto;
}

.timeline-sort-toggle {
  float: right;
}

.timeline-wrapper {
  position: relative;
}

.timeline-wrapper :deep(.ant-timeline) {
  padding-top: 20px;
}

.timeline-wrapper :deep(.ant-timeline-item) {
  position: relative;
  padding-bottom: 40px;
}

.timeline-wrapper :deep(.ant-timeline-item:last-child) {
  padding-bottom: 20px;
}

.timeline-wrapper :deep(.ant-timeline-item-tail) {
  border-left-style: dashed;
  border-left-width: 2px;
}

.timeline-dot-wrapper {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 50%;
}

.timeline-start-top,
.timeline-start-bottom,
.timeline-end-top,
.timeline-end-bottom {
  padding-left: 40px;
  color: #8c8c8c;
  font-size: 14px;
}

.timeline-start-top,
.timeline-end-top {
  margin-bottom: 10px;
}

.timeline-start-bottom,
.timeline-end-bottom {
  margin-top: 10px;
}

.timeline-start-top i,
.timeline-start-bottom i {
  margin-right: 6px;
  color: #52c41a;
}

.timeline-end-top i,
.timeline-end-bottom i {
  margin-right: 6px;
  color: #ff4d4f;
}

.timeline-content {
  padding: 8px 0;
  margin-left: 8px;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.activity-title-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.activity-type {
  font-weight: 600;
  color: #262626;
  font-size: 14px;
}

.activity-btn-name {
  font-weight: 400;
  color: #1890ff;
  font-size: 12px;
  margin-left: 2px;
}

.activity-time {
  color: #8c8c8c;
  font-size: 12px;
  white-space: nowrap;
}

.activity-details {
  margin: 8px 0;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.activity-detail {
  color: #595959;
  font-size: 14px;
  margin-bottom: 8px;
}

.activity-metadata {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* 스크롤바 스타일 */
.timeline-container::-webkit-scrollbar {
  width: 6px;
}

.timeline-container::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.timeline-container::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 3px;
}

.timeline-container::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

/* 버튼 스타일 */
.yellow-button {
  background-color: #FFD814 !important;
  border-color: #FFD814 !important;
}

.yellow-button:hover {
  background-color: #E5C112 !important;
  border-color: #E5C112 !important;
}

/* 원본 데이터 스타일 */
.rawdata-container {
  padding: 16px 0;
  height: calc(100vh - 350px);
  display: flex;
  flex-direction: column;
}

.json-viewer {
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 16px;
  overflow: auto;
  flex: 1;
}

.json-viewer pre {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
}

/* JSON 색상 스타일 */
.json-key {
  color: #881391;
  font-weight: 500;
}

.json-string {
  color: #0a3069;
}

.json-number {
  color: #0550ae;
}

.json-boolean {
  color: #cf222e;
  font-weight: 600;
}

.json-null {
  color: #6e7781;
  font-weight: 600;
}

.json-bracket {
  color: #24292f;
  font-weight: 600;
}

/* 접기/펼치기 스타일 */
.json-toggle {
  cursor: pointer;
  user-select: none;
  display: inline-block;
  width: 16px;
  color: #6e7781;
  font-size: 11px;
  font-family: monospace;
  text-align: center;
  margin-right: 2px;
}

.json-toggle:hover {
  background-color: rgba(139, 148, 158, 0.1);
  border-radius: 2px;
}

.json-ellipsis {
  color: #6e7781;
  font-style: italic;
  font-weight: normal;
  padding: 0 4px;
}

/* 스크롤바 스타일 */
.json-viewer::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.json-viewer::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.json-viewer::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 4px;
}

.json-viewer::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

/* 메시지 이력 스타일 */
.message-container {
  padding: 16px 0;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

.message-info {
  margin-top: 12px;
}

.message-template {
  color: #595959;
  font-size: 13px;
  margin-bottom: 8px;
}

.message-content {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  margin: 8px 0;
}

.message-text {
  color: #262626;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.message-interaction {
  margin-top: 8px;
}

.message-error {
  margin-top: 12px;
}

/* 메시지 스크롤바 스타일 */
.message-container::-webkit-scrollbar {
  width: 6px;
}

.message-container::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.message-container::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 3px;
}

.message-container::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

/* 리뷰 정보 스타일 */
.review-container {
  padding: 16px 0;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.review-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: box-shadow 0.3s;
}

.review-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.review-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.review-score {
  font-weight: 600;
  color: #262626;
  margin-left: 8px;
}

.review-date {
  color: #8c8c8c;
  font-size: 13px;
}

.review-content {
  margin: 16px 0;
  padding: 12px;
  background: #fafafa;
  border-radius: 4px;
}

.review-content p {
  margin: 0;
  color: #262626;
  line-height: 1.6;
}

.review-content.no-content p {
  color: #8c8c8c;
  font-style: italic;
}

.satisfaction-points {
  margin: 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.points-label {
  font-weight: 500;
  color: #595959;
  margin-right: 4px;
}

.review-meta {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  font-size: 13px;
  color: #8c8c8c;
}

.review-meta i {
  margin-right: 4px;
}

/* 리뷰 스크롤바 스타일 */
.review-container::-webkit-scrollbar {
  width: 6px;
}

.review-container::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.review-container::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 3px;
}

.review-container::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

.yellow-button {
  background-color: #FFD814;
  border-color: #FFD814;
}

.yellow-button:hover {
  background-color: #E5C112;
  border-color: #E5C112;
}

.ml-2 {
  margin-left: 8px;
}

/* 마이페이지 스타일 */
.mypage-container {
  padding: 0;
}

/* 마이페이지 헤더 - 스크린샷 스타일 참고 */
.mypage-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  padding: 24px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.profile-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  position: relative;
}

.profile-avatar {
  background-color: #e8e8e8;
  color: #999;
}

.profile-info h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #262626;
}

.profile-info p {
  margin: 4px 0 0;
  font-size: 14px;
  color: #8c8c8c;
}

.refresh-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

/* 빠른 메뉴 스타일 - 스크린샷 참고 */
.quick-menu {
  display: flex;
  gap: 24px;
  justify-content: space-around;
  padding: 20px;
  background: #fafafa;
  border-radius: 12px;
}

.menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;
  padding: 8px;
  border-radius: 8px;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
}

.menu-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  position: relative;
}

.coupon-icon {
  background: linear-gradient(135deg, #c3b5ff 0%, #9c88ff 100%);
  color: white;
}

.booking-icon {
  background: linear-gradient(135deg, #a8d8ff 0%, #74b9ff 100%);
  color: white;
}

.cert-icon {
  background: linear-gradient(135deg, #ffd93d 0%, #ffb81c 100%);
  color: white;
}

.menu-icon .badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ff4757;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.menu-item span {
  font-size: 13px;
  color: #595959;
  font-weight: 500;
}

/* 정보 카드 스타일 */
.info-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
}

.info-card :deep(.ant-card-head) {
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.info-card :deep(.ant-card-head-title) {
  font-size: 15px;
  font-weight: 600;
}

.mypage-container .ant-descriptions-item-label {
  background-color: #fafafa;
  font-weight: 500;
  color: #595959;
}

.mypage-container .ant-descriptions-item-content {
  color: #262626;
}

/* 반려동물 섹션 */
.animal-section {
  margin-bottom: 16px;
}

.animal-section h5 {
  font-size: 14px;
  font-weight: 600;
  color: #595959;
  margin-bottom: 12px;
}

.animal-section h5 i {
  margin-right: 6px;
  color: #8c8c8c;
}

/* 테이블 스타일 */
.mypage-container .ant-table-wrapper {
  margin-top: 8px;
}

.mypage-container .ant-table-thead > tr > th {
  background: #fafafa;
  font-weight: 600;
  font-size: 13px;
}

.mypage-container .ant-table-tbody > tr > td {
  font-size: 13px;
}

.mb-3 {
  margin-bottom: 16px;
}

.mt-2 {
  margin-top: 8px;
}

.json-viewer-simple {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  line-height: 1.4;
}
</style>